import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { UserPreferenceContext } from '@contexts/user-preference-context';
import { SupportedDeviceType } from '@enums/supported-device-type.enum';
import { UploadType } from '@enums/upload-type.enum';
import UploadService from '@services/uploads.service';
import {
  toastify,
  useTranslation,
  useInputTextValidationSchema,
  handleFieldUpdate,
  FIELD_NAMES,
} from '@testsigma/atoms';
import { Upload } from '@testsigmainc/models';
import { handleApiErrorMessages } from '@utils/api-handler-utils';
import classNames from 'classnames';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IosAppSigningModal } from '../ios-app-signing-modal/ios-app-signing.modal';
import { UploadActionButtonsPanel } from './components/action-buttons';
import { UploadFileNamePanel } from './components/file-name-panel';
import { FileSelectorPanel } from './components/file-selector-panel';
import { UploadFileVersionPanel } from './components/file-version-panel';
import { UploadModalHeaderPanel } from './components/header-panel';
import { IOSDeviceTypeSelectionPanel } from './components/supported-ios-devices-panel';
import { IOSKeychainSupportPanel } from './components/ios-keychain-support-panel';
import { SkipResignPanel } from './components/skip-resign-panel';
import type { FormData } from '@shared/components/filters/types';
interface FormPros {
  upload?: Upload;
  onClose?: () => void;
  onSuccess: (uploadId?: number) => void;
  isContentOnly?: boolean;
  applicationId?: number;
  applicationVersionId?: number;
  isStrictMode?: boolean;
  isForceUpload?: boolean;
}

export const Form: React.FC<FormPros> = ({
  upload = new Upload(),
  onSuccess,
  onClose,
  isContentOnly,
  applicationId,
  applicationVersionId,
  isStrictMode = false,
  isForceUpload = false,
}) => {
  const { t } = useTranslation();
  const abortController = useRef(new AbortController());
  const { selectedApplicationId } = useContext(UserPreferenceContext)
    ?.userPreference?.preference?.projectPreference;

  const [newFile, setNewFile] = useState<File>();
  const [fileName, setFileName] = useState<string>(upload?.name);
  const [uploadVersion, setUploadVersion] = useState<string>(
    upload?.latestVersion?.name,
  );
  const [supportedDeviceType, setSupportedDeviceType] =
    useState<SupportedDeviceType>(upload?.supportedDeviceType);
  const [iosKeychainSupport, setIosKeychainSupport] = useState<boolean>(
    upload?.latestVersion?.iosKeychainSupport ?? false,
  );
  const [skipResign, setSkipResign] = useState<boolean>(
    upload?.latestVersion?.skipResign ?? false,
  );
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [uploadId, setUploadId] = useState<number>();
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const fileNameDefaultSchema = useInputTextValidationSchema(FIELD_NAMES.NAME);
  const schema = yup.object().shape({
    name: fileNameDefaultSchema,
  });
  const {
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: upload?.name,
    },
  });

  const onFileChange = useCallback(
    (file: File) => {
      setNewFile(file);
      // do not set filename when it's version update
      if (!upload?.id) {
        setFileName(file?.name);
        handleFieldUpdate({ setValue, fieldName: 'name', value: file?.name });
      }
      setUploadVersion(file.name);
    },
    [upload],
  );
  const onChangeFileName = useCallback(
    (filename) => {
      setFileName(filename);
      handleFieldUpdate({ setValue, fieldName: 'name', value: filename });
    },
    [fileName],
  );

  const isIpaFile = useMemo(() => {
    if (newFile) {
      return newFile?.name?.slice(-3).toLowerCase() == 'ipa';
    } else if (upload?.id) {
      return upload.latestVersion?.uploadType == UploadType.IPA;
    }
  }, [newFile, upload]);

  const isAppFile = useMemo(() => {
    if (newFile) {
      return newFile?.name?.slice(-7).toLowerCase() == 'app.zip';
    } else if (upload?.id) {
      return upload.latestVersion?.uploadType == UploadType.APP;
    }
  }, [newFile, upload]);
  // constructs create/update payload
  const getFormData = () => {
    const formData = new FormData();
    formData.append('fileContent', newFile || new File([''], ''));
    formData.append('name', fileName);
    const currentApplicationId = applicationId || selectedApplicationId;
    formData.append('applicationId', `${currentApplicationId}`);
    formData.append('version', uploadVersion);
    formData.append('forceUpload', isForceUpload + '');

    const ext = newFile?.name?.split('.').pop();
    if (ext?.toLowerCase() === 'apk') {
      formData.append('uploadType', UploadType.APK);
    } else if (ext?.toLowerCase() === 'aab') {
      formData.append('uploadType', UploadType.AAB);
    } else if (isIpaFile) {
      formData.append('uploadType', UploadType.IPA);
      formData.append(
        'supportedDeviceType',
        supportedDeviceType || SupportedDeviceType.IOS_REAL_DEVICE,
      );
      formData.append('iosKeychainSupport', String(iosKeychainSupport));
      formData.append('skipResign', String(skipResign));
    } else if (isAppFile) {
      formData.append('uploadType', UploadType.APP);
      formData.append('supportedDeviceType', SupportedDeviceType.IOS_SIMULATOR);
    } else {
      formData.append('uploadType', UploadType.Attachment);
    }
    return formData;
  };
  // service call to create new upload
  const onCreate = useCallback(() => {
    setIsUploadInProgress(true);
    const formData = getFormData();
    const uploadApi =
      window.location !== window.parent?.location
        ? UploadService.create
        : UploadService.createWithCancel;

    uploadApi(formData, abortController.current)
      .then((res) => {
        if (res.supportedDeviceType === SupportedDeviceType.IOS_REAL_DEVICE) {
          setOpen(true);
          setId(res.latestVersionId);
          setUploadId(res.id);
        } else {
          onSuccess(res.id);
        }
      })
      .catch((err) => {
        handleApiErrorMessages(err, t);
      })
      .finally(() => {
        setIsUploadInProgress(false);
      });
  }, [
    newFile,
    fileName,
    uploadVersion,
    selectedApplicationId,
    applicationId,
    supportedDeviceType,
    iosKeychainSupport,
    skipResign,
  ]);
  const [id, setId] = useState<number>();
  const [open, setOpen] = useState(false);

  // service call to update upload
  const onUpdate = useCallback(() => {
    setIsUploadInProgress(true);
    const formData = getFormData();
    UploadService.updateWithCancel(upload.id, formData, abortController.current)
      .then((res: any) => {
        if (res.supportedDeviceType === SupportedDeviceType.IOS_REAL_DEVICE) {
          setOpen(true);
          setId(res.latestVersionId);
          setUploadId(res.id);
          setIsUpdate(true);
          setWdaId(res.latestVersion.webDriverAgentId);
        } else {
          onSuccess();
        }
      })
      .catch((err) => {
        handleApiErrorMessages(err, t);
      })
      .finally(() => {
        setIsUploadInProgress(false);
      });
  }, [
    upload,
    newFile,
    fileName,
    uploadVersion,
    selectedApplicationId,
    applicationId,
    supportedDeviceType,
    iosKeychainSupport,
    skipResign,
  ]);
  const [wdaId, setWdaId] = useState<number>(null);

  const onCancel = useCallback(() => {
    if (isUploadInProgress) {
      abortController.current.abort();
      toastify('Upload has been cancelled.', {
        type: 'info',
      });
    }
    setFileName(null);
    setUploadVersion(null);
    setNewFile(null);
    onClose();
  }, [onClose, isUploadInProgress]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isUploadInProgress) {
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploadInProgress]);

  // validate upload form
  const hasError = useMemo(() => {
    const fileSizeLimit = 1024 * 1024 * 1024;
    const hasfileNameError = !!errors.name;
    const trimmedVersion = uploadVersion?.trim();
    return (
      hasfileNameError ||
      !trimmedVersion ||
      trimmedVersion.length < 1 ||
      uploadVersion.length > 256 ||
      (!newFile && !upload.id) ||
      (newFile && newFile.size > fileSizeLimit) ||
      (upload.id && !newFile)
    );
  }, [fileName, uploadVersion, newFile, upload, errors?.name]);

  const canShowAction: boolean = Boolean(
    !isContentOnly || (isContentOnly && (upload?.name || newFile?.name)),
  );
  return (
    <>
      <IosAppSigningModal
        isOpen={open}
        setIsOpen={setOpen}
        id={id}
        onSuccess={onSuccess}
        uploadId={uploadId}
        isUpdate={isUpdate}
        wdaId={wdaId}
        setWdaId={setWdaId}
      />
      <div
        className={classNames('p-6 gap-y-6 flex flex-col', {
          'w-125': !isContentOnly,
          'w-full': isContentOnly,
        })}
      >
        {/* Header */}
        {Boolean(!isContentOnly) && (
          <UploadModalHeaderPanel isCreateNew={!upload.id} />
        )}

        {/* File Upload */}
        <FileSelectorPanel
          onFileChange={onFileChange}
          applicationVersionId={applicationVersionId}
          isStrictMode={isStrictMode}
        />

        {/* Name of the upload */}
        {(upload?.name || newFile?.name) && (
          <UploadFileNamePanel
            onChangeFileName={onChangeFileName}
            isUploadInProgress={isUploadInProgress}
            setValue={setValue}
            errors={errors}
            watch={watch}
          />
        )}

        {/* Upload version */}
        {newFile?.name && (
          <UploadFileVersionPanel
            fileVersion={uploadVersion}
            onChangeFileVersion={setUploadVersion}
            isUploadInProgress={isUploadInProgress}
          />
        )}

        {/* iOS device type */}
        {isIpaFile && (
          <>
            <IOSDeviceTypeSelectionPanel
              supportedDeviceType={supportedDeviceType}
              onChangeSupportedDeviceType={setSupportedDeviceType}
              isUploadVersion={!!upload?.id}
            />
            <IOSKeychainSupportPanel
              iosKeychainSupport={iosKeychainSupport}
              onChangeIosKeychainSupport={setIosKeychainSupport}
            />
            <SkipResignPanel
              skipResign={skipResign}
              onChangeSkipResign={setSkipResign}
            />
          </>
        )}

        {/* Action buttons */}
        {canShowAction && (
          <div className='mt-4'>
            <UploadActionButtonsPanel
              isUploadInProgress={isUploadInProgress}
              isCreateNew={!upload?.id}
              onCancel={onCancel}
              onCreate={onCreate}
              onUpdate={onUpdate}
              hasError={hasError}
            />
          </div>
        )}
      </div>
    </>
  );
};
